/**
 * What the Journey Builder already knows before it asks anything.
 * (Citizen first run slice C · D8 "never ask twice") — shared by the web's
 * guided builder and the phone's three-question one (Citizen Mobile · D-M2).
 *
 * Where the citizen lives may already be on file (`talents.current_country`) —
 * from the profile's essential data or an invitation's review — and where they
 * are moving to is the destination open today, or the one on file. When both
 * are known the builder does not ask the route again; otherwise its countries
 * task asks it. (Its old "prefill" read a `user.profile` that `GET /users/me`
 * never returned, so it was always empty.)
 *
 * Returned as the guide's initial variables:
 *   • `originCountry` / `destinationCountry` — ISO codes, when known;
 *   • `originCountryName` / `destinationCountryName` — for the guide's copy;
 *   • `routeKnown` — `'yes'` skips the countries task (the guide config's
 *     `skip_if`); anything else asks;
 *   • `bringFamily` — the onboarding family answer, pre-selected in the
 *     household task (still asked, never skipped).
 */
import { openDestinationOf, soleOpenDestination } from '@tonnie/foundation';
import { builderFamilyAnswerFromOnboarding } from '@tonnie/types';

/**
 * The part of the citizen's talent profile the builder reads — structural, so
 * both apps pass their profile read (`useUserProfile().data.talent`) as it is.
 */
export interface BuilderTalent {
  currentCountry?: string | null;
  customFields?: Record<string, unknown> | null;
}

const ISO_COUNTRY = /^[A-Z]{2}$/;

export interface BuilderContext {
  originCountry?: string;
  destinationCountry?: string;
  originCountryName?: string;
  destinationCountryName?: string;
  /** The guide's `skip_if: routeKnown == 'yes'` reads this. A string: the engine compares strings. */
  routeKnown: 'yes' | 'no';
  bringFamily?: boolean;
}

function isoOrNull(value: unknown): string | null {
  return typeof value === 'string' && ISO_COUNTRY.test(value) ? value : null;
}

export function builderContextFrom(
  talent: BuilderTalent | null | undefined,
  countryName: (code: string) => string
): BuilderContext {
  const custom = talent?.customFields ?? {};
  const origin = isoOrNull(talent?.currentCountry);
  // The destination on file only when a journey can be built there today;
  // otherwise the one destination open today.
  const stored = isoOrNull(custom['targetCountry']);
  const destination = openDestinationOf(stored) ?? soleOpenDestination();

  const routeKnown = !!origin && !!destination && origin !== destination;
  const bringFamily = builderFamilyAnswerFromOnboarding(custom['familyConfig']);

  return {
    ...(origin && { originCountry: origin, originCountryName: countryName(origin) }),
    ...(destination && {
      destinationCountry: destination,
      destinationCountryName: countryName(destination),
    }),
    routeKnown: routeKnown ? 'yes' : 'no',
    ...(bringFamily !== null && { bringFamily }),
  };
}
